import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { LayoutDashboard, Users, CreditCard, Database, ServerCog, ShieldAlert, Tag, Mail, FileText, ClipboardList, Settings as SettingsIcon, Inbox } from "lucide-react";

export default function AdminLayout() {
  const { t } = useI18n();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="font-mono text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (!user?.is_admin) {
    return (
      <div className="App min-h-screen bg-background">
        <Header variant="app" />
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <ShieldAlert className="mx-auto h-12 w-12 text-destructive" />
          <h1 className="mt-6 font-display text-3xl font-black tracking-tight">{t("admin.adminOnly")}</h1>
          <p className="mt-2 text-muted-foreground">403</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-6 rounded-sm bg-foreground px-4 py-2 font-mono text-xs uppercase text-background"
            data-testid="admin-forbidden-back"
          >
            ← {t("nav.dashboard")}
          </button>
        </div>
      </div>
    );
  }

  const items = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: t("admin.nav.overview"), testid: "admin-nav-overview" },
    { to: "/admin/users", icon: Users, label: t("admin.nav.users"), testid: "admin-nav-users" },
    { to: "/admin/plans", icon: Tag, label: t("admin.nav.plans"), testid: "admin-nav-plans" },
    { to: "/admin/payments", icon: CreditCard, label: t("admin.nav.payments"), testid: "admin-nav-payments" },
    { to: "/admin/jobs", icon: Database, label: t("admin.nav.jobs"), testid: "admin-nav-jobs" },
    { to: "/admin/settings", icon: SettingsIcon, label: t("admin.nav.settings"), testid: "admin-nav-settings" },
    { to: "/admin/emails", icon: Mail, label: t("admin.nav.emails"), testid: "admin-nav-emails" },
    { to: "/admin/contact", icon: Inbox, label: "Contact", testid: "admin-nav-contact" },
    { to: "/admin/legal", icon: FileText, label: t("admin.nav.legal"), testid: "admin-nav-legal" },
    { to: "/admin/audit", icon: ClipboardList, label: t("admin.nav.audit"), testid: "admin-nav-audit" },
    { to: "/admin/system", icon: ServerCog, label: t("admin.nav.system"), testid: "admin-nav-system" },
  ];

  return (
    <div className="App min-h-screen bg-background">
      <Header variant="app" />
      <div className="border-b border-border bg-foreground text-background">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-6 w-6 items-center justify-center bg-[#002FA7] text-white font-mono text-[10px]">A</span>
            <span className="font-display text-sm font-bold tracking-tight">{t("admin.title")}</span>
          </div>
          <span className="font-mono text-[10px] uppercase tracking-wider text-background/60">admin::{user.email}</span>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-8 sm:px-6 lg:grid-cols-12 lg:px-8">
        <aside className="lg:col-span-3">
          <nav className="border border-border bg-card">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 border-b border-border px-4 py-3 text-sm transition last:border-0 ${
                    isActive
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:bg-accent/40 hover:text-foreground"
                  }`
                }
              >
                <it.icon className="h-4 w-4" />
                <span className="font-mono text-xs uppercase tracking-wider">{it.label}</span>
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="lg:col-span-9">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
